import React, { type ReactNode, useMemo } from "react";
import { View, StyleSheet } from "react-native";

import { Box } from "../Box";
import { Heading } from "../Heading";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";

interface EmptyStateProps {
  /**
   * Optional illustration to display at the top of the empty state.
   * This should be an image or icon component.
   */
  illustration?: ReactNode;

  /**
   * Main heading text for the empty state.
   */
  title?: string;

  /**
   * Optional descriptive text that provides more context about the empty state.
   */
  description?: string;

  /**
   * Optional action elements such as buttons to help users take next steps.
   * Can be a single button or multiple buttons wrapped in a container.
   */
  actions?: ReactNode;

  /**
   * Semantic heading level for accessibility (maps to aria-level / h1–h6).
   * Does not affect visual size — only screen reader semantics.
   *
   * @default 2
   */
  headingAriaLevel?: number;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

/**
 * EmptyState component displays a centered message with optional illustration and actions
 * when there is no content to show. The order is always: illustration → text → actions.
 *
 * @example
 * <EmptyState
 *   illustration={<MyIllustration />}
 *   title="No items found"
 *   description="Try adjusting your search criteria"
 *   actions={<Button onPress={handleRefresh}>Action</Button>}
 * />
 */
export function EmptyState({
  illustration,
  title,
  description,
  actions,
  headingAriaLevel = 2,
  sx,
}: EmptyStateProps) {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          paddingHorizontal: theme.spacing.s6,
          backgroundColor: theme.colors.background,
        },
        content: {
          alignItems: "center",
        },
        illustration: {
          marginBottom: theme.spacing.s2,
        },
        textContent: {
          alignItems: "center",
          paddingBottom: theme.spacing.s5,
          paddingTop: theme.spacing.s5,
        },
        title: {
          textAlign: "center",
          marginBottom: theme.spacing.s2,
          fontWeight: 700,
          color: theme.colors.foregroundStrong,
          alignSelf: "center",
        },
        description: {
          textAlign: "center",
          marginBottom: theme.spacing.s2,
          fontWeight: 400,
          color: theme.colors.foregroundStrong,
        },
        actions: {
          alignItems: "center",
          width: "100%",
        },
      }),
    [theme],
  );

  return (
    <View style={[styles.container, sx]}>
      <Box sx={styles.content}>
        {illustration && <Box sx={styles.illustration}>{illustration}</Box>}

        <Box sx={styles.textContent}>
          {title && (
            <Heading level={4} ariaLevel={headingAriaLevel} sx={styles.title}>
              {title}
            </Heading>
          )}

          {description && (
            <Text level={3} sx={styles.description} variant="contrast">
              {description}
            </Text>
          )}
        </Box>
        {actions && <Box sx={styles.actions}>{actions}</Box>}
      </Box>
    </View>
  );
}
